/**
 * BarChartCard Component
 *
 * Reusable Bar Chart with Korat brand colors and responsive design
 */

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts'

// Korat brand colors
const COLORS = {
  navy: '#1e3a5f',
  orange: '#f97316',
  blue: '#2c5282',
  lightOrange: '#fb923c',
}

export interface BarChartData {
  name: string
  value: number
  color?: string
}

export interface BarChartCardProps {
  title: string
  description?: string
  data: BarChartData[]
  xAxisLabel?: string
  yAxisLabel?: string
  color?: 'navy' | 'orange' | 'blue' | 'gradient'
  height?: number
  horizontal?: boolean
  showGrid?: boolean
  isLoading?: boolean
}

export function BarChartCard({
  title,
  description,
  data,
  xAxisLabel,
  yAxisLabel,
  color = 'navy',
  height = 300,
  horizontal = false,
  showGrid = true,
  isLoading = false,
}: BarChartCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-2 animate-pulse" />
        {description && <div className="h-4 bg-slate-100 rounded w-2/3 mb-4 animate-pulse" />}
        <div className="flex flex-col gap-3" style={{ height }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-slate-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>
          ไม่มีข้อมูล
        </div>
      </div>
    )
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null

    const data = payload[0]

    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-korat-navy-700">{data.name || data.payload.name}</p>
        <p className="text-xs text-slate-600 mt-1">
          {new Intl.NumberFormat('th-TH').format(data.value)}
        </p>
      </div>
    )
  }

  const getBarColor = (index: number, item: BarChartData) => {
    if (item.color) return item.color

    if (color === 'gradient') {
      // Alternating navy and orange
      return index % 2 === 0 ? COLORS.navy : COLORS.orange
    }

    return COLORS[color] || COLORS.navy
  }

  // Format large numbers for Y-axis (Thai style)
  const formatYAxis = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} ล้าน`
    if (value >= 100_000) return `${(value / 100_000).toFixed(1)} แสน`
    if (value >= 10_000) return `${(value / 10_000).toFixed(1)} หมื่น`
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)} พัน`
    return new Intl.NumberFormat('th-TH').format(value)
  }

  // Format numbers for data labels
  const formatDataLabel = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
    if (value >= 100_000) return `${(value / 100_000).toFixed(0)}K`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
    return new Intl.NumberFormat('th-TH').format(value)
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 5, right: 20, left: horizontal ? 80 : 0, bottom: 5 }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}

          {horizontal ? (
            <>
              <XAxis type="number" tickFormatter={formatYAxis} tick={{ fontSize: 13, fill: '#64748b', fontWeight: 500 }} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 13, fill: '#334155', fontWeight: 500 }}
                width={100}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 13, fill: '#334155', fontWeight: 500 }}
                label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fontSize: 13, fill: '#64748b', fontWeight: 500 }}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
              />
            </>
          )}

          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />

          <Bar dataKey="value" radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(index, entry)} />
            ))}
            <LabelList
              dataKey="value"
              position={horizontal ? 'right' : 'top'}
              formatter={formatDataLabel}
              content={(props: any) => {
                const { x, y, width, value } = props
                const displayValue = formatDataLabel(value)
                return (
                  <g>
                    {/* Background rectangle */}
                    <rect
                      x={horizontal ? x + width + 4 : x + width / 2 - 25}
                      y={horizontal ? y - 10 : y - 24}
                      width={50}
                      height={20}
                      fill="white"
                      stroke="#e2e8f0"
                      strokeWidth={1}
                      rx={4}
                    />
                    {/* Text */}
                    <text
                      x={horizontal ? x + width + 29 : x + width / 2}
                      y={horizontal ? y : y - 10}
                      fill="#1e293b"
                      fontSize={13}
                      fontWeight={700}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {displayValue}
                    </text>
                  </g>
                )
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
